import { useState } from 'react';
import { mutate } from 'swr';
import useSWR from 'swr';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';

type GroupData = {
  id: string;
  name: string;
};

const AddParticipantButton = ({ className }: { className?: string }) => {
  const { data: groupsData } = useSWR<{ groups: GroupData[] }>('/api/groups');
  const groups = groupsData?.groups ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    groupId?: string;
  }>({});

  const handleOpen = () => {
    setFirstName('');
    setLastName('');
    setGroupId(groups.length === 1 ? groups[0].id : '');
    setErrors({});
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setFirstName('');
    setLastName('');
    setGroupId('');
    setErrors({});
  };

  const handleSubmit = async () => {
    const newErrors: {
      firstName?: string;
      lastName?: string;
      groupId?: string;
    } = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'Feld ist erforderlich';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Feld ist erforderlich';
    }
    if (!groupId) {
      newErrors.groupId = 'Feld ist erforderlich';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/createParticipant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          groupId,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        mutate('/api/participants');
        if (result.alreadyExisted) {
          toast({
            title: 'Teilnehmer existiert bereits',
            description: `Startnummer ${result.participant.number} ist bereits vergeben.`,
            status: 'info',
          });
        } else {
          toast({
            title: 'Teilnehmer erstellt!',
            status: 'success',
          });
        }
        handleClose();
      } else {
        toast({
          title: 'Fehler beim Erstellen des Teilnehmers!',
          status: 'error',
        });
      }
    } catch (err) {
      toast({
        title: 'Fehler beim Erstellen des Teilnehmers!',
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button className={className} onClick={handleOpen}>
        Teilnehmer hinzufügen
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Teilnehmer hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-participant-firstname">
                Vorname <span className="text-red-500">*</span>
              </Label>
              <Input
                id="create-participant-firstname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Vorname"
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-participant-lastname">
                Nachname <span className="text-red-500">*</span>
              </Label>
              <Input
                id="create-participant-lastname"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Nachname"
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-participant-group">
                Stamm <span className="text-red-500">*</span>
              </Label>
              <Select
                id="create-participant-group"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                disabled={groups.length <= 1}
              >
                <option value="">Stamm auswählen</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
              {errors.groupId && (
                <p className="text-sm text-red-500">{errors.groupId}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" className="mr-3" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Wird erstellt...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddParticipantButton;
